import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowRight } from 'lucide-react';
import PartnersSection from '../components/PartnersSection';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqData } from '../mock/data';
import SEO from '../components/SEO';

const AboutPage = () => {
  const stats = [
    { value: '1500+', label: 'Clients Abonnés' },
    { value: '4.8', label: 'sur 5 étoiles' },
    { value: '10+', label: "Années d'expérience" }
  ];

  return (
    <div className="pt-20">
      <SEO
        title="À propos — CarMaintenance, l'abonnement automobile suisse pensé par des experts"
        description="Découvrez le projet CarMaintenance : un abonnement automobile clair, accessible et tout inclus, porté par des experts du métier en Suisse."
        path="/a-propos-de-nous"
        image="/generated/innovation-confiance.jpg"
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-bottom duration-700">
              Le projet
            </h1>
            <p className="text-2xl text-gray-600 max-w-3xl mx-auto">
              Un projet initié par des <span className="text-[#52a675] font-semibold">experts du métier</span>
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-4">
              Enfin un abonnement qui couvre tous les frais liés aux réparations, à la maintenance et à l'entretien de votre véhicule.
            </p>
          </div>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT — illustration */}
            <div className="order-1 lg:order-1">
              <div className="relative max-w-md mx-auto lg:max-w-none">
                <img
                  src="/generated/innovation-confiance.jpg"
                  alt="Illustration de l'écosystème CarMaintenance : entretien, confiance, abonnement, suivi digital"
                  className="w-full h-auto rounded-3xl shadow-xl ring-1 ring-black/5"
                  loading="lazy"
                />
                {/* Subtle decorative blob */}
                <div
                  className="absolute -z-10 -inset-4 bg-[#52a675]/10 rounded-3xl blur-2xl"
                  aria-hidden="true"
                />
              </div>
            </div>

            {/* RIGHT — title + tabs */}
            <div className="order-2 lg:order-2">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3 text-center lg:text-left">
                L'innovation au service de <span className="text-[#52a675]">votre véhicule</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 text-center lg:text-left">
                Une nouvelle manière de prendre soin de votre véhicule, portée par ceux qui les connaissent le mieux.
              </p>

              {/* Mission, Vision, Values Tabs */}
              <Tabs defaultValue="mission" className="w-full">
                <TabsList className="grid w-full grid-cols-3 h-auto p-1 mb-8">
                  <TabsTrigger value="mission" className="text-base py-2.5 data-[state=active]:shadow-sm">Mission</TabsTrigger>
                  <TabsTrigger value="vision" className="text-base py-2.5 data-[state=active]:shadow-sm">Vision</TabsTrigger>
                  <TabsTrigger value="values" className="text-base py-2.5 data-[state=active]:shadow-sm">Valeurs</TabsTrigger>
                </TabsList>

                <TabsContent value="mission" className="mt-4">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 lg:p-8 rounded-2xl shadow-lg">
                    <p className="text-base lg:text-lg text-gray-700 leading-relaxed">
                      Simplifier l'entretien automobile en offrant un service d'abonnement clair, accessible et tout inclus. Nous nous engageons à prendre en charge tous les besoins d'entretien et de réparation des véhicules, pour que nos clients puissent rouler l'esprit tranquille, sans surprises ni frais cachés.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="vision" className="mt-4">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 lg:p-8 rounded-2xl shadow-lg">
                    <p className="text-base lg:text-lg text-gray-700 leading-relaxed">
                      Devenir la référence en Suisse (et au-delà) en matière d'entretien automobile par abonnement. Notre ambition est de transformer durablement la relation entre conducteurs et garagistes, en apportant plus de transparence, de confiance et de confort à chaque propriétaire de véhicule.
                    </p>
                  </div>
                </TabsContent>

                <TabsContent value="values" className="mt-4">
                  <div className="bg-gradient-to-br from-gray-50 to-white p-6 lg:p-8 rounded-2xl shadow-lg">
                    <ul className="space-y-3">
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#52a675] rounded-full mt-2 shrink-0"></div>
                        <div>
                          <strong className="text-gray-900">Transparence :</strong>
                          <span className="text-gray-700"> Des prix clairs, sans frais cachés.</span>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#52a675] rounded-full mt-2 shrink-0"></div>
                        <div>
                          <strong className="text-gray-900">Confiance :</strong>
                          <span className="text-gray-700"> Une relation honnête, bâtie sur la fiabilité de notre service.</span>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#52a675] rounded-full mt-2 shrink-0"></div>
                        <div>
                          <strong className="text-gray-900">Simplicité :</strong>
                          <span className="text-gray-700"> Un abonnement facile à comprendre, facile à utiliser.</span>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#52a675] rounded-full mt-2 shrink-0"></div>
                        <div>
                          <strong className="text-gray-900">Qualité :</strong>
                          <span className="text-gray-700"> Des professionnels qualifiés, des pièces fiables, un service irréprochable.</span>
                        </div>
                      </li>
                      <li className="flex items-start space-x-3">
                        <div className="w-2 h-2 bg-[#52a675] rounded-full mt-2 shrink-0"></div>
                        <div>
                          <strong className="text-gray-900">Accessibilité :</strong>
                          <span className="text-gray-700"> Que vous rouliez peu ou beaucoup, notre service s'adapte à vous.</span>
                        </div>
                      </li>
                    </ul>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Quelques <span className="text-[#52a675]">chiffres</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Animés par l'innovation et le goût du travail bien fait, nous réunissons des expertises variées au service de chaque projet.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg text-center hover:shadow-xl transition-all duration-300"
              >
                <div className="text-5xl font-bold text-[#52a675] mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <PartnersSection />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Combien coûterait un abonnement pour votre véhicule ?
            </h2>
            <Link to="/abonnements">
              <Button
                size="lg"
                className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 group whitespace-normal h-auto max-w-full"
              >
                Simuler un abonnement maintenant
                <ArrowRight className="ml-2 w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Additional CTA Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-[#52a675] to-[#3d8a5c] rounded-3xl p-8 text-white shadow-xl">
              <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
                Compte entreprise
              </span>
              <h3 className="text-3xl font-bold mt-4 mb-6">
                Vous souhaitez couvrir une flotte de véhicules ?
              </h3>
              <Link to="/contact">
                <Button variant="secondary" className="bg-white text-[#52a675] hover:bg-gray-100 shadow-lg group">
                  Obtenez un devis
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 text-white shadow-xl">
              <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
                Vous êtes garagiste ?
              </span>
              <h3 className="text-3xl font-bold mt-4 mb-6">
                Vous souhaitez devenir franchisé CarMaintenance ?
              </h3>
              <Link to="/contact">
                <Button variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg group">
                  Contactez-nous
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              FAQs
            </h2>
            <p className="text-lg text-gray-600">
              Vous avez des questions concernant CarMaintenance ? Nous avons rassemblé ici les réponses aux questions les plus fréquentes
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="border border-gray-200 rounded-lg px-6 hover:border-[#52a675] transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#52a675] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Toutes les questions</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Une question ? N'hésitez pas à nous contacter.
          </h2>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              Contactez-nous
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
